import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUIBox from "@mui/material/Box";
import MUIStack from "@mui/material/Stack";
import MUITextField from "@mui/material/TextField";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITypography from "@mui/material/Typography";
import MUIIconButton from "@mui/material/IconButton";
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker as MUIDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import MUILocalShippingIcon from "@mui/icons-material/LocalShipping";
import MUIOpenInNewIcon from "@mui/icons-material/OpenInNew";
import Card from "../../Card";
import InfoItem from "../../InfoItem";
import ChipLocation from "../../Chip/ChipLocation";
import User from "../../../Utility/Crud/User";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import Patient from "../../../Utility/Crud/Patient";
import Security from "../../../Utility/Security";

/**
 * CardChartDelivery renders a Card component displaying order approval
 * information.
 * @returns {JSX.Element}
 */

const title = "Delivery";
const icon = MUILocalShippingIcon;

const className = "CardChartDelivery";

export default function CardChartDelivery(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);

    const [editMode, setEditMode] = useState(false);
    const [cardChart, setCardChart] = useState(null);

    const patient = chartContext.chart.patient[chartContext.patientId];
    const order = chartContext.chart.orderActivity[chartContext.orderId];
    const orderActivityApprovals = Object.values(chartContext.chart.orderActivityApprovals).find(orderActivityApproval => orderActivityApproval.orderActivityId === chartContext.orderId);
    const originalOrderActivityApprovals = Object.values(chartContext.originalChart.orderActivityApprovals).find(orderActivityApproval => orderActivityApproval.orderActivityId === chartContext.orderId);
    const approval = Object.values(chartContext.chart?.approval || {}).find(approval => approval?.id === orderActivityApprovals?.approvalsId);
    const originalApproval = Object.values(chartContext.originalChart?.approval || {}).find(approval => approval?.id === originalOrderActivityApprovals?.approvalsId);

    const originalOrderActivity = chartContext.originalChart.orderActivity[chartContext.orderId];

    const activity = chartContext.chart.activity[chartContext.orderId];

    const parentOrder = OrderActivity.getParentOrder(chartContext.orderId, chartContext.chart) || null;
    const parentOrderProducts = OrderActivity.getProducts(parentOrder, chartContext.chart);
    const primaryProduct = OrderActivity.getPrimaryProduct(parentOrderProducts);
    const primaryProductDisplayName = OrderActivity.getProductDisplayName(primaryProduct, mainContext.baseUnitsIndexed, mainContext.vendorsIndexed, mainContext.productSubTypesIndexed);

    const [newScheduledDeliveryNoteId, setNewScheduledDeliveryNoteId] = useState(null);
    const [newDeliverByDateNoteId, setNewDeliverByDateNoteId] = useState(null);

    // Close edit mode if originalChart changes (on save)
    useEffect(() => {
        setEditMode(false);
        setCardChart(null);
    }, [chartContext.originalChart]);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    useEffect(() => {
        setNewDeliverByDateNoteId(chartContext.getNegativeId());
        setNewScheduledDeliveryNoteId(chartContext.getNegativeId());
    }, []);

    /**
     * Creates a note if the scheduled delivery date has changed.
     */
    useEffect(() => {
        let noteText = "";

        // Check if scheduledDeliveryDate changed
        if (cardChart?.scheduledDeliveryDate &&
            originalOrderActivity?.scheduledDeliveryDate !== cardChart?.scheduledDeliveryDate
        ) {
            if (originalOrderActivity?.scheduledDeliveryDate) {
                // Date was changed from one value to another
                noteText += "Scheduled delivery date changed from " +
                    moment(originalOrderActivity.scheduledDeliveryDate).format("M/D/YYYY") +
                    " to " +
                    moment(cardChart.scheduledDeliveryDate).format("M/D/YYYY");
            } else {
                // Date was originally null and now set
                noteText += "Scheduled delivery date set to " +
                    moment(cardChart.scheduledDeliveryDate).format("M/D/YYYY");
            }

            // Add technician info if available
            if (order?.technicianId) {
                const technician = Object.values(mainContext.usersIndexed).find(user => user.id === order.technicianId);
                if (technician) {
                    noteText += " with technician " + User.getDisplayName(technician);
                }
            }
        }

        // Create note if there's any text to add and the scheduled delivery date has changed
        if (noteText &&
            originalOrderActivity?.scheduledDeliveryDate !== cardChart?.scheduledDeliveryDate
        ) {
            const newNote = {
                [newScheduledDeliveryNoteId]: {
                    id: newScheduledDeliveryNoteId,
                    accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
                    activityId: chartContext.orderId,
                    assignedToId: null,
                    completed: true,
                    createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
                    createdAt: new Date().toISOString(),
                    dueDate: null,
                    isAutoVerification: false,
                    patientName: Patient.getDisplayName(patient),
                    task: false,
                    text: noteText,
                    type: "ActivityNote",
                }
            }
            chartContext.onChange({ note: newNote });
        } else {
            // Delete the note if it exists (e.g., when reverting changes)
            if (chartContext.chart.note && chartContext.chart.note[newScheduledDeliveryNoteId]) {
                chartContext.onChange({ note: { [newScheduledDeliveryNoteId]: undefined } });
            }
        }

    }, [order?.technicianId, cardChart?.scheduledDeliveryDate])

    /**
     * Creates a note if the deliver by date has changed.
     */
    useEffect(() => {
        let noteText = "";

        // Check if deliveryDate changed
        if (cardChart?.deliverByDate &&
            originalApproval?.deliverByDate !== cardChart?.deliverByDate
        ) {
            if (noteText) noteText += ". ";

            if (originalApproval?.deliverByDate) {
                // Date was changed from one value to another
                noteText += "Delivery date changed from " +
                    moment(originalApproval.deliverByDate).format("M/D/YYYY") +
                    " to " +
                    moment(cardChart.deliverByDate).format("M/D/YYYY");
            } else {
                // Date was originally null and now set
                noteText += "Delivery date set to " +
                    moment(cardChart.deliverByDate).format("M/D/YYYY");
            }
        }

        // Create note if there's any text to add and the deliver by date has changed
        if (noteText &&
            originalApproval?.deliverByDate !== cardChart?.deliverByDate
        ) {
            const newNote = {
                [newDeliverByDateNoteId]: {
                    id: newDeliverByDateNoteId,
                    accountId: OrderActivity.getAccount(order, chartContext.chart)?.id,
                    activityId: chartContext.orderId,
                    assignedToId: null,
                    completed: true,
                    createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
                    createdAt: new Date().toISOString(),
                    dueDate: null,
                    isAutoVerification: false,
                    patientName: Patient.getDisplayName(patient),
                    task: false,
                    text: noteText,
                    type: "ActivityNote",
                }
            }
            chartContext.onChange({ note: newNote });
        } else {
            // Delete the note if it exists (e.g., when reverting changes)
            if (chartContext.chart.note && chartContext.chart.note[newDeliverByDateNoteId]) {
                chartContext.onChange({ note: { [newDeliverByDateNoteId]: undefined } });
            }
        }

    }, [cardChart?.deliverByDate])

    /**
     * Returns the styles for a container that should be full width at a given max width.
     * Only used when sidebar=true to handle responsive container queries.
     * @param {number} maxWidth - The max width at which the container should be full width.
     * @returns {Object} The styles for the container.
     */
    const getContainerQuery = (maxWidth) => {
        if (!props.sidebar) {
            return {}
        }

        return {
            [`@container (max-width: ${maxWidth}px)`]: { flexBasis: '100% !important', maxWidth: '100% !important' }
        }
    }

    /**
    * Renders the current model textfield.
    * @returns {JSX.Element} Grid layout with current model textfield
    */
    const renderCurrentModelTextfield = () => {
        return (
            <MUIGrid item lg={3} md={3} xs={3}>
                <MUITextField
                    autoFocus={true}
                    value={order?.currentModel || ""}
                    size="small"
                    label="Current Model"
                    variant="standard"
                    fullWidth={true}
                    inputProps={{
                        maxLength: 255,
                    }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { currentModel: e.target.value } } })
                        setCardChart({ ...cardChart, currentModel: e.target.value })
                    }}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the serial number textfield.
     * @returns {JSX.Element} Grid layout with serial number textfield
     */
    const renderSerialNumberTextfield = () => {
        const parentOrder = OrderActivity.getParentOrder(chartContext.orderId, chartContext.chart) || null;

        return (
            <MUIGrid item lg={3} md={3} xs={3}>
                <MUITextField
                    value={activity?.type !== "Order" ? parentOrder?.serialNumber : order?.serialNumber || ""}
                    size="small"
                    label="Serial Number"
                    variant="standard"
                    fullWidth={true}
                    inputProps={{
                        maxLength: 255,
                    }}
                    disabled={activity?.type !== "Order"}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { serialNumber: e.target.value } } })
                        setCardChart({ ...cardChart, serialNumber: e.target.value })
                    }}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the expected ship date picker.
     * @returns {JSX.Element} Grid layout with expected ship date picker
     */
    const renderExpectedShipDatePicker = () => {
        return (
            <MUIGrid item lg={3} md={3} xs={3}>
                <MUIDatePicker
                    label="Expected Ship Date"
                    value={order.expectedShipDate ? moment(order.expectedShipDate) : null}
                    slotProps={{
                        field: {
                            clearable: true,
                        },
                        textField: {
                            fullWidth: true,
                            size: "small",
                            variant: 'standard',
                            InputLabelProps: {
                                shrink: true
                            },
                        },
                    }}
                    onChange={(value) => {
                        const expectedShipDate = value
                            ? moment(value).format('YYYY-MM-DDT00:00:00')
                            : null;
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { expectedShipDate: expectedShipDate } } })
                        setCardChart({ ...cardChart, expectedShipDate: expectedShipDate })
                    }}
                    views={['year', 'month', 'day']}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the deliver by date picker.
     * @param {boolean} shouldUseOrderLayout - Whether the field should occupy one quarter of the Order row.
     * @returns {JSX.Element} Grid layout with deliver by date picker
     */
    const renderDeliverByDatePicker = (shouldUseOrderLayout = false) => {
        let deliverByDateDisabled = true;

        if (Security.hasPermission("order.deliverByDate")) {
            deliverByDateDisabled = false;
        }
        return (
            <MUIGrid
                item
                lg={3}
                md={shouldUseOrderLayout ? 3 : 6}
                xs={shouldUseOrderLayout ? 3 : 12}
                sx={shouldUseOrderLayout ? null : getContainerQuery(600)}
            >
                <MUIDatePicker
                    label="Deliver By Date"
                    defaultValue={approval && approval.deliverByDate ? moment(approval.deliverByDate) : null}
                    disabled={deliverByDateDisabled}
                    slotProps={{
                        field: {
                            clearable: true,
                        },
                        textField: {
                            fullWidth: true,
                            size: "small",
                            variant: 'standard',
                            InputLabelProps: {
                                shrink: true
                            },
                        },
                    }}
                    onChange={(value) => {
                        chartContext.onChange({
                            approval: {
                                [approval?.id]: {
                                    deliverByDate: value
                                        ? moment(value).format('YYYY-MM-DDT00:00:00')
                                        : null,
                                },
                            },
                        });
                        setCardChart({
                            ...cardChart,
                            deliverByDate: value
                                ? moment(value).format('YYYY-MM-DDT00:00:00')
                                : null,
                        });
                    }}
                    views={['year', 'month', 'day']}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the scheduled delivery date picker.
     * @param {boolean} shouldUseOrderLayout - Whether the field should occupy one quarter of the Order row.
     * @returns {JSX.Element} Grid layout with scheduled delivery date picker
     */
    const renderScheduledDeliveryDatePicker = (shouldUseOrderLayout = false) => {
        let dateDisabled = true;
        if (Security.hasPermission("order.updateScheduledDeliveryDate")) {
            dateDisabled = false;
        } else if (
            order?.status === 2 ||
            order?.status === 3
        ) {
            dateDisabled = false;
        }

        const maxDeliverByDate = approval && approval.deliverByDate
            ? moment.utc(approval.deliverByDate).local()
            : null;

        return (
            <MUIGrid
                item
                lg={3}
                md={shouldUseOrderLayout ? 3 : 6}
                xs={shouldUseOrderLayout ? 3 : 12}
                sx={shouldUseOrderLayout ? null : getContainerQuery(600)}
            >
                <MUIDateTimePicker
                    disabled={dateDisabled}
                    maxDate={maxDeliverByDate}
                    label="Scheduled Delivery"
                    value={order?.scheduledDeliveryDate ? moment.utc(order.scheduledDeliveryDate).local() : null}
                    onOpen={
                        () => {
                            if (!order?.scheduledDeliveryDate) {
                                const newDate = maxDeliverByDate ? moment(maxDeliverByDate).clone().hour(12).minute(0).second(1).millisecond(1) : moment().hour(12).minute(0).second(1).millisecond(1);
                                const formattedDate = newDate ? moment.utc(newDate).format('YYYY-MM-DDTHH:mm:ss') : null;
                                chartContext.onChange({ orderActivity: { [chartContext.orderId]: { scheduledDeliveryDate: formattedDate } } })
                                setCardChart({ ...cardChart, scheduledDeliveryDate: formattedDate })
                            }
                        }
                    }
                    slotProps={{
                        field: {
                            clearable: true,
                        },
                        textField: {
                            fullWidth: true,
                            size: "small",
                            variant: 'standard',
                            InputLabelProps: {
                                shrink: true
                            },
                        }
                    }}
                    onChange={(value) => {
                        let newDate = null;
                        if (value !== null) {
                            newDate = moment(value).clone();
                            newDate.hour(value.hour()).minute(value.minute()).second(0).millisecond(0);
                        }

                        const formattedDate = newDate ? moment.utc(newDate).format('YYYY-MM-DDTHH:mm:ss') : null;
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { scheduledDeliveryDate: formattedDate } } })
                        setCardChart({ ...cardChart, scheduledDeliveryDate: formattedDate })
                    }}
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    shouldDisableDate={(date) => {
                        if (!approval || !approval.deliverByDate) {
                            return false;
                        }

                        const deliverByDate = moment(approval.deliverByDate);
                        return date.isAfter(deliverByDate, 'day');
                    }}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the technician dropdown.
     * @param {boolean} shouldUseOrderLayout - Whether the field should occupy one quarter of the Order row.
     * @returns {JSX.Element} Grid layout with technician dropdown
     */
    const renderTechnicianDropdown = (shouldUseOrderLayout = false) => {
        const getTechnicianOptions = () => {
            const technicians = User.getTechnicians(
                mainContext.usersIndexed,
                mainContext.userprofileLocationsIndexed,
                null
            );
            return technicians.map((user, index) => ({
                value: user.id,
                label: User.getDisplayName(user),
                key: index,
            }));
        }
        return (
            <MUIGrid
                item
                lg={3}
                md={shouldUseOrderLayout ? 3 : 6}
                xs={shouldUseOrderLayout ? 3 : 12}
                sx={shouldUseOrderLayout ? null : getContainerQuery(600)}
            >
                <MUIAutocomplete
                    options={getTechnicianOptions()}
                    disabled={order?.status !== 2 && order?.status !== 3} //Order status Ready to Deliver (2) or Setup (3)
                    value={getTechnicianOptions().find(option => option.value === order.technicianId) || null}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    getOptionLabel={(option) => option?.label || ''}
                    getOptionKey={(option) => option?.key || ''}
                    onChange={(e, newValue) => {
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { technicianId: newValue?.value || null } } })
                        setCardChart({ ...cardChart, technicianId: newValue?.value || null })
                    }}
                    renderInput={(params) => (
                        <MUITextField
                            {...params}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            variant="standard"
                            label="Technician"
                            fullWidth
                        />
                    )}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the base unit textfield.
     * @returns {JSX.Element} Grid layout with base unit textfield
     */
    const renderBaseUnitTextfield = () => {
        return (
            <MUIGrid item lg={3} md={6} xs={12} sx={getContainerQuery(600)}>
                <MUITextField
                    value={primaryProductDisplayName || ""}
                    size="small"
                    label="Base Unit"
                    variant="standard"
                    fullWidth={true}
                    inputProps={{
                        maxLength: 255,
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the setup date picker.
     * @param {boolean} shouldUseOrderLayout - Whether the field should occupy one quarter of the Order row.
     * @returns {JSX.Element} Grid layout with setup date picker
     */
    const renderSetupDatePicker = (shouldUseOrderLayout = false) => {
        return (
            <MUIGrid
                item
                lg={3}
                md={shouldUseOrderLayout ? 3 : 6}
                xs={shouldUseOrderLayout ? 3 : 12}
                sx={shouldUseOrderLayout ? null : getContainerQuery(600)}
            >
                <MUIDatePicker
                    label="Setup Date"
                    value={order?.setupDate ? moment.utc(order.setupDate).local() : null}
                    slotProps={{
                        field: {
                            clearable: false,
                        },
                        textField: {
                            fullWidth: true,
                            size: "small",
                            variant: 'standard',
                            InputLabelProps: {
                                shrink: true
                            },
                        },
                    }}
                    onChange={(value) => {
                        const formattedDate = value ? moment.utc(value).hour(12).minute(0).second(0).format('YYYY-MM-DDTHH:mm:ss') : null;
                        chartContext.onChange({ orderActivity: { [chartContext.orderId]: { setupDate: formattedDate } } })
                        setCardChart({ ...cardChart, setupDate: formattedDate })
                    }}
                    views={['year', 'month', 'day']}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the delivery note textfield.
     * @returns {JSX.Element} Grid layout with delivery note textfield
     */
    const renderDeliveryNoteTextfield = () => {
        const handleDeliveryNoteChange = (updatedFields) => {
            chartContext.onChange({ orderActivity: { [chartContext.orderId]: { deliveryNotes: updatedFields.deliveryNotes } } });
            setCardChart({ ...cardChart, deliveryNotes: updatedFields.deliveryNotes });
        }
        return (
            <MUIGrid item xs={12}>
                <MUITextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Delivery Note"
                    value={order?.deliveryNotes || ""}
                    onChange={(e) => {
                        handleDeliveryNoteChange({ deliveryNotes: e.target.value });
                    }}
                    variant="standard"
                    inputProps={{
                        style: { resize: "both" }
                    }}
                    InputLabelProps={{ shrink: true }}
                />
            </MUIGrid>
        )
    }

    /**
     * Renders the edit view displaying patient approval information.
     * @returns {JSX.Element} Grid layout with patient information display
     */
    const renderEditView = () => {
        if (activity?.type !== "Order") {
            return (
                <MUIGrid container spacing={2} rowSpacing={3} sx={{ containerType: 'inline-size' }}>
                    {renderDeliverByDatePicker()}
                    {renderScheduledDeliveryDatePicker()}
                    {renderTechnicianDropdown()}
                    {renderSetupDatePicker()}
                    {renderDeliveryNoteTextfield()}
                </MUIGrid>
            )
        }
        return (
            <MUIGrid container spacing={2} rowSpacing={3} sx={{ containerType: 'inline-size' }}>
                {renderCurrentModelTextfield()}
                {renderSerialNumberTextfield()}
                {renderSetupDatePicker(true)}
                <MUIGrid item lg={3} md={3} xs={3} />
                {renderDeliverByDatePicker(true)}
                {renderExpectedShipDatePicker()}
                {renderScheduledDeliveryDatePicker(true)}
                {renderTechnicianDropdown(true)}
                {renderDeliveryNoteTextfield()}
            </MUIGrid>
        )
    }

    /**
     * Renders the read-only view displaying approval information.
     * @returns {JSX.Element} Grid layout with approval information display
     */
    const renderReadOnlyView = () => {
        return (
            <MUIBox>
                {activity?.type !== "Order" ? (
                    <>
                        <MUIBox
                            sx={{
                                bgcolor: "background.default",
                                borderRadius: 1,
                                p: 2,
                                mb: 2,
                            }}
                        >
                            <MUIStack direction="row" spacing={0.5} sx={{ mb: 1, alignItems: "center" }}>
                                <MUITypography variant="body2" color="text.secondary">Parent Order</MUITypography>
                                <MUIIconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => window.open(`/chart/${chartContext.patientId}/order/${parentOrder.id}`, "_blank")}
                                >
                                    <MUIOpenInNewIcon fontSize="small" />
                                </MUIIconButton>
                            </MUIStack>
                            <MUIGrid container spacing={props.sidebar ? 1 : 4} rowSpacing={2}>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Base Unit"}
                                        value={
                                            <>
                                                {primaryProductDisplayName || "None"}
                                                {primaryProduct?.hcpcs && (
                                                    <>
                                                        <br />
                                                        {primaryProduct.hcpcs}
                                                    </>
                                                )}
                                            </>
                                        }
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Serial Number"}
                                        value={primaryProduct?.serial || "None"}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Setup Date"}
                                        value={parentOrder?.setupDate ? moment.utc(parentOrder?.setupDate).local().format("M/D/YYYY") : "None"}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Sales Location"}
                                        value={<ChipLocation location={mainContext.locationsIndexed[parentOrder?.salesLocationId]} />}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Service Location"}
                                        value={<ChipLocation location={mainContext.locationsIndexed[parentOrder?.serviceLocationId]} />}
                                    />
                                </MUIGrid>
                                <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                    <InfoItem
                                        label={"Billing Location"}
                                        value={<ChipLocation location={mainContext.locationsIndexed[parentOrder?.billingLocationId]} />}
                                    />
                                </MUIGrid>
                            </MUIGrid>
                        </MUIBox>
                        <MUIGrid container spacing={props.sidebar ? 1 : 4} rowSpacing={2}>
                            <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                <InfoItem
                                    label={"Deliver By Date"}
                                    value={approval?.deliverByDate ? moment(approval?.deliverByDate).format("M/D/YYYY") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                <InfoItem
                                    label={"Scheduled Delivery"}
                                    value={order?.scheduledDeliveryDate ? moment.utc(order?.scheduledDeliveryDate).local().format("M/D/YYYY h:mma") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                <InfoItem
                                    label={"Technician"}
                                    value={User.getDisplayName(Object.values(mainContext.usersIndexed).find(user => user.id === order.technicianId)) || "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={props.sidebar ? 6 : "auto"} sm={props.sidebar ? 12 : "auto"} md={props.sidebar ? 4 : "auto"}>
                                <InfoItem
                                    label={"Setup Date"}
                                    value={order?.setupDate ? moment.utc(order?.setupDate).local().format("M/D/YYYY") : "None"}
                                />
                            </MUIGrid>
                        </MUIGrid>
                        <MUIGrid container spacing={props.sidebar ? 0 : 4} rowSpacing={2} sx={{ mt: .5, width: "100%", flexWrap: "wrap" }}>
                            <MUIGrid item xs={12} sx={{ width: "100%", overflow: "visible", flexGrow: 1 }}>
                                <MUITypography variant="subtitle2">
                                    Delivery Note
                                </MUITypography>
                                <MUITypography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        width: "100%",
                                        overflowWrap: "anywhere",
                                        minHeight: "1.5em",
                                        display: "block",
                                    }}
                                >
                                    {order?.deliveryNotes || "None"}
                                </MUITypography>
                            </MUIGrid>
                        </MUIGrid>
                    </>
                ) : (
                    <>
                        <MUIGrid container spacing={props.sidebar ? 1 : 4} rowSpacing={2}>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Current Model"}
                                    value={order.currentModel || "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Serial Number"}
                                    value={order.serialNumber || "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Setup Date"}
                                    value={order.setupDate ? moment.utc(order.setupDate).local().format("M/D/YYYY") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3} />
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Deliver By Date"}
                                    value={approval?.deliverByDate ? moment(approval?.deliverByDate).format("M/D/YYYY") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Expected Ship Date"}
                                    value={order.expectedShipDate ? moment(order.expectedShipDate).format("M/D/YYYY") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Scheduled Delivery"}
                                    value={order.scheduledDeliveryDate ? moment.utc(order.scheduledDeliveryDate).local().format("M/D/YYYY h:mma") : "None"}
                                />
                            </MUIGrid>
                            <MUIGrid item xs={3} sm={3} md={3}>
                                <InfoItem
                                    label={"Technician"}
                                    value={User.getDisplayName(Object.values(mainContext.usersIndexed).find(user => user.id === order.technicianId)) || "None"}
                                />
                            </MUIGrid>
                        </MUIGrid>
                        <MUIGrid container spacing={props.sidebar ? 0 : 4} rowSpacing={2} sx={{ mt: .5, width: "100%", flexWrap: "wrap" }}>
                            <MUIGrid item xs={12} sx={{ width: "100%", overflow: "visible", flexGrow: 1 }}>
                                <MUITypography variant="subtitle2">
                                    Delivery Note
                                </MUITypography>
                                <MUITypography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        width: "100%",
                                        overflowWrap: "anywhere",
                                        minHeight: "1.5em",
                                        display: "block",
                                    }}
                                >
                                    {order?.deliveryNotes || "None"}
                                </MUITypography>
                            </MUIGrid>
                        </MUIGrid>
                        {approval?.fieldNotes ? (
                            <MUIGrid container spacing={props.sidebar ? 0 : 4} rowSpacing={2} sx={{ mt: .5, width: "100%", flexWrap: "wrap" }}>
                                <MUIGrid item xs={12} sx={{ width: "100%", overflow: "visible", flexGrow: 1 }}>
                                    <MUITypography variant="subtitle2">
                                        Field Notes
                                    </MUITypography>
                                    <MUITypography
                                        variant="body2"
                                        sx={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            width: "100%",
                                            overflowWrap: "anywhere",
                                            minHeight: "1.5em",
                                            display: "block",
                                        }}
                                    >
                                        {approval?.fieldNotes}
                                    </MUITypography>
                                </MUIGrid>
                            </MUIGrid>
                        ) : null}
                    </>
                )}

            </MUIBox>
        )
    }

    /**
     * Determines which view to render based on edit mode state. Renders search
     * box, add note field, notes table, or empty state.
     * @returns {JSX.Element} Either edit view or read-only view
     */
    const renderContent = () => {
        if (editMode) {
            return (
                renderEditView()
            )
        }
        return (
            renderReadOnlyView()
        );
    }

    /**
     * Returns the action buttons for the card component.
     * @returns {Array} Array of action button components
     */
    const getActions = () => {
        const handleCancel = () => {
            const updatedOrderActivity = { ...order };
            const updatedApproval = { ...approval };
            const deliveryFields = cardChart ? Object.keys(cardChart) : [];

            for (const field of deliveryFields) {
                if (order[field] !== originalOrderActivity[field]) {
                    updatedOrderActivity[field] = originalOrderActivity[field];
                }
                if (approval[field] !== originalApproval[field]) {
                    updatedApproval[field] = originalApproval[field];
                }
            }

            // Delete any notes with negative IDs (newly created notes)
            const notesToDelete = {};
            [newScheduledDeliveryNoteId, newDeliverByDateNoteId].forEach(noteId => {
                if (chartContext.chart.note && chartContext.chart.note[noteId]) {
                    notesToDelete[noteId] = undefined;
                }
            });

            if (Object.keys(notesToDelete).length > 0) {
                chartContext.onChange({ note: notesToDelete });
            }
            chartContext.onChange({
                orderActivity: { [chartContext.orderId]: { ...updatedOrderActivity } },
                approval: { [approval?.id]: { ...updatedApproval } },
            });
            chartContext.setCardEditMode?.(className, false);
            setEditMode(false);

            const clearedIssues = { ...chartContext.validationIssues };
            delete clearedIssues[className];
            chartContext.updateValidationIssues(clearedIssues);
            setCardChart(null);
        }

        return [
            ...(editMode ? [
                <MUIButton
                    color="error"
                    disabled={chartContext.isUpdating}
                    onClick={handleCancel}
                    size="small"
                >
                    Cancel
                </MUIButton>
            ] : [
                <MUIButton
                    disabled={chartContext.isUpdating}
                    onClick={() => setEditMode(true)}
                    size="small"
                >
                    Edit
                </MUIButton>
            ])
        ];
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartDelivery.meta = { className: className, title: title, icon: icon };


